import React, { useEffect } from 'react';

import { Link, Menu, MenuItem, PopoverPosition } from '@mui/material';
import { writePsd } from 'ag-psd';

import { GET_IMAGE } from '~/api/request';
import { Loader } from '~/components/Loader';
import { useNotification } from '~/contexts/NotificationContext';
import { useSidePanel } from '~/contexts/SidePanelContext';
import { useVehicleOptions } from '~/contexts/VehicleOptionsContext';
import { ICamera } from '~/domain/ICamera';
import { IImageSettings } from '~/domain/IImageSettings';
import { APP_DOMAIN, PSD_AOV_LAYERS, SITE_CLIENT } from '~/env';
import { IContextMenu } from '~/hooks/useContextMenu';
import { useImageUrl } from '~/hooks/useImage/useImageUrl';
import { useRequest } from '~/hooks/useRequestQuery';
import { useRequestUrl } from '~/hooks/useRequestUrl';
import { useTrigger } from '~/hooks/useTrigger';
import { MakeURLFriendly } from '~/pages/urls';

export interface IImageContextMenu {
  anchorPosition: PopoverPosition | undefined,
  context: IContextMenu;
  camera: ICamera;
  images: { label?: string, settings: IImageSettings }[];
}

const PSD_LAYERS = PSD_AOV_LAYERS.split(',');

const createCanvas = async (settings: IImageSettings, url?: string) => {
  const src = url ? await GET_IMAGE(url, {}) : null;
  return new Promise<HTMLCanvasElement>((resolve) => {
    const { width, height } = settings;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    if (src) {
      const image = new Image();
      image.crossOrigin = 'Anonymous';
      image.src = src;
      image.onload = () => {
        const context = canvas.getContext('2d');
        if (context) {
          context.drawImage(image, 0, 0);
        }
        resolve(canvas);
      };
    } else {
      resolve(canvas);
    }
  });
}

const updateURLParameter = (url: string, param: string, paramVal: string) => {
  let newAdditionalURL = '';
  let tempArray = url.split('?');
  const baseURL = tempArray[0];
  const additionalURL = tempArray[1];
  let temp = '';
  if (additionalURL) {
    tempArray = additionalURL.split('&');
    for (let i=0; i<tempArray.length; i++){
      if(tempArray[i].split('=')[0] !== param){
        newAdditionalURL += temp + tempArray[i];
        temp = '&';
      }
    }
  }

  const rows_txt = temp + '' + param + '=' + paramVal;
  return baseURL + '?' + newAdditionalURL + rows_txt;
}

const createLayer = async (settings: IImageSettings, src: string, aov: string) => {
  // aov is either car, background, or shadow

  // car only
  // &resp=png&bkgnd=transparent&tracelayer=ignore(shadow_diffuse,background)

  // shadow only
  // &resp=png&bkgnd=transparent&tracelayer=export(shadow_diffuse)

  // background only
  // &resp=png&bkgnd=5&tracelayer=export(background)

  let aov_params = '';

  switch (aov) {
  case 'Base':
    aov_params = '&resp=png&tracelayer=ignore(shadow_diffuse,background)';
    src = updateURLParameter(src, 'bkgnd', 'transparent');
    break;
  case 'Shadow':
    aov_params = '&resp=png&tracelayer=export(shadow_diffuse)';
    src = updateURLParameter(src, 'bkgnd', 'transparent');
    break;
  case 'BackgroundOnly':
    aov_params = '&resp=png&tracelayer=export(background)';
    src = updateURLParameter(src, 'bkgnd', '5');
    break;
  }

  const { width, height } = settings;
  return {
    top: 0,
    left: 0,
    bottom: height,
    right: width,
    name: aov,
    canvas: await createCanvas(settings, `${src}${aov_params}&ft=png`),
    // set any other metadata in here
  };
}

const convert = async (settings: IImageSettings, src: string) => {
  const { width, height } = settings;
  const psd = {
    width,
    height,
    canvas: await createCanvas(settings),
    children: await Promise.all(PSD_LAYERS.map(aov => createLayer(settings, src, aov))),
  };

  const buffer = writePsd(psd);
  const blob = new Blob([buffer], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  return url;
}

export const ImageContextMenu: React.FC<IImageContextMenu> = (props) => {
  const { anchorPosition, context, camera, images } = props;
  const request = useRequest();
  const vehicle = request?.vehicleModel?.value;
  const requestUrl = useRequestUrl();
  const imageUrl = useImageUrl();
  const notify = useNotification();
  const vehicleOptions = useVehicleOptions();
  const { eim } = vehicleOptions;
  const features = vehicleOptions.features.join(',');
  const { issueId } = useSidePanel();
  const [isExporting, exportHook] = useTrigger();

  useEffect(() => {
    /// last will be the highest quality available
    const { settings } = images[images.length - 1];
    const src = imageUrl(camera, settings);
    const { cameraWaypoint } = settings;
    const psd = `${vehicle}-${eim}-${camera.name}${cameraWaypoint ? `-${cameraWaypoint}` : ''}.psd`;
    let isMounted = true;
    const unmount = () => {
      if (isExporting && isMounted) {
        exportHook.deactivate();
        context.deactivate();
        isMounted = false;
      }
    };
    const exec = async () => {
      const href = await convert(settings, src);
      const link = document.createElement('a');
      link.download = psd;
      link.href = href;
      link.click();
      unmount();
    }
    if (isExporting) {
      exec();
    }
    return unmount;
  }, [camera, context, eim, exportHook, imageUrl, images, isExporting, vehicle])

  return (
    <Menu
      open={!!anchorPosition}
      onClose={context.deactivate}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onContextMenu={(e) => {
        // prevent right click when menu is already opened
        e.preventDefault();
        context.deactivate();
      }}
    >
      <MenuItem
        disabled={isExporting}
        onClick={async () => {
          let url;
          if (features) {
            url = requestUrl({ f: features, camera: MakeURLFriendly(camera.name), issueId });
          } else {
            url = requestUrl({ camera: MakeURLFriendly(camera.name), issueId });
          }

          await navigator.clipboard.writeText(`${APP_DOMAIN}${url}`);
          notify({ type: 'success', message: 'URL copied to clipboard' });
          context.deactivate();
        }}
      >
        Copy URL
      </MenuItem>
      {images.map(({ label, settings }) => {
        return (((SITE_CLIENT === 'Subaru' && label && label.indexOf('4K') === -1) || (SITE_CLIENT !== 'Subaru')) && 
          <MenuItem
            key={label || ''}
            component={Link}
            href={imageUrl(camera, settings)}
            disabled={isExporting}
            target="_blank"
            onClick={async () => {
              context.deactivate();
            }}
          >
            {`Open ${label ? `${label} ` : ''}Image in New Tab`}
          </MenuItem>
        );
      })}
      { SITE_CLIENT !== 'Subaru' && 
        <MenuItem
          disabled={isExporting}
          onClick={exportHook.activate}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          Export PSD
          {isExporting && <Loader sx={{ m: '0 10px' }} size={20} border="2px solid #5980FF" />}
        </MenuItem>
      }
    </Menu>
  );
}
